/**
 * Mobile recorder worklists (VoiceKolect; APP_AGENT §10, §15).
 *
 * Role-shaped and gold-stripped. A **team recorder** (`can_record`) gets the
 * segment work for their team/languoid — both **claimable** (`READY_TO_RECORD`)
 * and **in-progress** (`RECORDING`) challenges, so a freshly-published challenge
 * is visible to be claimed and recorded on the device. An **project question
 * recorder** (`can_record_questions`) gets the English question work across the
 * languoids assigned on their project membership, for challenges already in
 * `RECORDING` (the segment recorder's claim opens the recording window). Audio is
 * per challenge (§0): one SEGMENT take + one QUESTION take.
 *
 * FIREWALL (§11.3): these expose the recorder's *script* — the segment context
 * and each utterance's text/speaker/register — and the English question, but
 * never gold (reference translations/answers, levels, challenge descriptions).
 */

import { z } from 'zod';

/** Status of a per-challenge take: not started, a draft in progress, or shipped. */
export interface WorkTake {
  status: 'NONE' | 'DRAFT' | 'CONFIRMED';
  /** Duration of the confirmed take, when one exists. */
  durationMs: number | null;
}

/** A spoken line for the recorder to read (gold-stripped). */
export interface WorkUtterance {
  ordinal: number;
  speakerId: string;
  registerLabel: string;
  utteranceContext: string;
  text: string;
  /** Romanized transliteration of `text` for two-script languoids (mal_Mlym); null otherwise. */
  textTransliteration: string | null;
}

/** One segment to record, in the team's language (team recorder). */
export interface RecordingWorkItem {
  challengeId: string;
  teamId: string;
  segmentId: string;
  languoidCode: string;
  domain: string;
  segmentContext: string;
  /**
   * Where the challenge sits in the recording stage. `READY_TO_RECORD` means it's
   * unclaimed — the app must `claim` it (→ RECORDING) before posting takes;
   * `RECORDING` means it's already claimed and can be recorded directly.
   */
  state: 'READY_TO_RECORD' | 'RECORDING';
  /**
   * Who has claimed the SEGMENT take (anti-duplication marker), or null. The app
   * shows "claimed by …" / "you"; the claim is advisory (others may take over).
   */
  claimedById: string | null;
  /** Bumps when the challenge is edited; the app caches/diffs by this (§10). */
  setVersion: string;
  segmentTake: WorkTake;
  /**
   * When the SEGMENT take was last seen uploaded to Drive (ISO), or null if no
   * segment file is there yet. Lets the recorder see his own work landed (and
   * avoid a needless retake). Stamped by the sync from Drive folder presence, so
   * it lags the cadence and never reverts to null.
   */
  segmentUploadedAt: string | null;
  /**
   * When the QUESTION take was last seen uploaded to Drive (ISO), or null. Advisory
   * coordination so the segment recorder knows whether the question is already done.
   */
  questionUploadedAt: string | null;
  /**
   * The language's shared Google Drive folder id to upload the take into
   * (APP_AGENT_UPDATE.md). Null when the language has no Drive sync source
   * configured — recording is then BLOCKED (Drive-only; there is no server-upload
   * fallback). The take file is named `<SEGMENT_ID>.wav`.
   */
  driveFolderId: string | null;
  /**
   * When the challenge was flagged from the mobile app (ISO), or null. The app hides
   * flagged challenges from its worklist; toggle via `/me/challenges/:id/flag`.
   */
  flaggedAt: string | null;
  /** The script: the utterances the recorder reads, concatenated, as one take. */
  utterances: WorkUtterance[];
}

/** One English question to record (project question recorder). */
export interface QuestionWorkItem {
  challengeId: string;
  teamId: string;
  segmentId: string;
  /** The segment's language (the question audio itself is always English). */
  languoidCode: string;
  domain: string;
  questionText: string;
  /**
   * Where the challenge sits — questions are claimable/recordable from
   * `READY_TO_RECORD` (independent of the segment recorder) through `RECORDING`,
   * plus `READY_TO_TEST` as a safety net (a question added after recording that
   * still needs its take — recordable in place, no state change).
   */
  state: 'READY_TO_RECORD' | 'RECORDING' | 'READY_TO_TEST';
  /** Who has claimed the QUESTION take (anti-duplication marker), or null. */
  claimedById: string | null;
  setVersion: string;
  questionTake: WorkTake;
  /**
   * When the SEGMENT take was last seen uploaded to the language's Drive folder
   * (ISO), or null if no segment file is there yet. Advisory coordination only —
   * the question is recordable regardless of the segment — it lets the app show
   * "segment uploaded" vs "segment not recorded". Stamped by the sync from Drive
   * folder presence, so it lags the cadence and never reverts to null.
   */
  segmentUploadedAt: string | null;
  /**
   * When the QUESTION take was last seen uploaded to Drive (ISO), or null. Lets the
   * app flag a question that's already recorded so it isn't re-recorded. Same Drive
   * folder presence / lag semantics as `segmentUploadedAt`.
   */
  questionUploadedAt: string | null;
  /**
   * The language's shared Google Drive folder id to upload the question take into
   * (APP_AGENT_UPDATE.md). Null when the language has no Drive sync source. The
   * take file is named `<SEGMENT_ID>q.wav`.
   */
  driveFolderId: string | null;
  /**
   * When the challenge was flagged from the mobile app (ISO), or null. The app hides
   * flagged challenges from its worklist; toggle via `/me/challenges/:id/flag`.
   */
  flaggedAt: string | null;
}

// --- Flag (mobile hide/skip a challenge) -------------------------------------

/** Body for flagging a challenge from the mobile app — the note is optional. */
export const challengeFlagSchema = z.object({
  note: z.string().max(500).optional(),
});
export type ChallengeFlagInput = z.infer<typeof challengeFlagSchema>;

/**
 * The flag state returned by flag/unflag. The app filters its worklists on `flagged`
 * (the worklist items also carry `flaggedAt`), so a flagged challenge can be hidden.
 */
export interface ChallengeFlagStatus {
  challengeId: string;
  flagged: boolean;
  flaggedAt: string | null;
}
